import { useEffect, useState } from 'react';
import { localeUI } from '../lib/format';
import { CloudHail, FileText, Satellite, Wind } from 'lucide-react';
import { api } from '../lib/api';
import { useI18n } from '../lib/i18n';
import { uploadFile } from '../lib/upload';
import { FileDrop } from './FileDrop';

type ReportFile = { id: string; file_name: string | null; doc_type: string; doc_label: string | null; ai_summary: string | null; created_at: string; uploaded_by: string | null };
const isWeatherFile = (f: ReportFile) => f.doc_type === 'weather_report' || /weather|storm|hail|noaa|nws|hailtrace/i.test(`${f.doc_label ?? ''} ${f.file_name ?? ''}`);

type Storm = {
  date: string;
  type: 'hail' | 'wind';
  eventType: string;
  distanceMiles: number;
  hailSizeInches?: number | null;
  windMph?: number | null;
};
type Weather = { cached: boolean; storms: Storm[]; main_storm_date: string | null; fetched_at: string | null; no_geo?: boolean };

/** Severidad simple para colorear. */
function severity(s: Storm): { label: string; color: string } {
  if (s.type === 'hail') {
    const h = s.hailSizeInches ?? 0;
    if (h >= 2) return { label: 'Severe', color: '#c0392b' };
    if (h >= 1) return { label: 'Significant', color: '#b7791f' };
    return { label: 'Minor', color: 'var(--muted)' };
  }
  const w = s.windMph ?? 0;
  if (w >= 70) return { label: 'Severe', color: '#c0392b' };
  if (w >= 58) return { label: 'Significant', color: '#b7791f' };
  return { label: 'Minor', color: 'var(--muted)' };
}

/**
 * Weather report (NOAA, 3 años, 5 millas). On-demand (NOAA es pesado): GET trae
 * lo cacheado; el botón consulta y cachea. La más reciente = Potential Storm; se
 * puede fijar una como tormenta principal del claim.
 */
export function ClaimWeather({ claimId }: { claimId: string }) {
  const { t } = useI18n();
  const [w, setW] = useState<Weather | null>(null);
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState<'all' | 'wind' | 'hail'>('all');
  const [tab, setTab] = useState<'noaa' | 'others'>('noaa');
  const [reports, setReports] = useState<ReportFile[]>([]);
  const [busyUp, setBusyUp] = useState(false);

  async function loadCached() {
    try { setW(await api.get<Weather>(`/api/claims/${claimId}/weather`)); } catch { /* ignore */ }
  }
  async function loadReports() {
    try { setReports((await api.get<{ files: ReportFile[] }>(`/api/claims/${claimId}/files`)).files.filter(isWeatherFile)); } catch { /* ignore */ }
  }
  useEffect(() => { loadCached(); loadReports(); /* eslint-disable-next-line */ }, [claimId]);

  // Sube un reporte de clima: la IA lo clasifica; si es weather_report cae en la pestaña Others.
  async function uploadReport(file: File) {
    setBusyUp(true);
    try { const key = await uploadFile(file, 'misc'); await api.post(`/api/claims/${claimId}/files`, { fileKey: key, fileName: file.name, mimeType: file.type || null }); await loadReports(); }
    catch { alert(t('wx.uploadFail')); } finally { setBusyUp(false); }
  }

  async function fetchNow() {
    setBusy(true);
    try { setW(await api.post<Weather>(`/api/claims/${claimId}/weather`, {})); } catch { alert(t('wx.fail')); } finally { setBusy(false); }
  }
  // Fija la tormenta como principal → también queda como Date of Loss del claim (con quién la puso).
  async function setMain(date: string, type: Storm['type']) {
    try { await api.post(`/api/claims/${claimId}/weather/main`, { date, type }); await loadCached(); } catch { /* ignore */ }
  }

  const allStorms = w?.storms ?? [];
  const windCount = allStorms.filter((s) => s.type === 'wind').length;
  const hailCount = allStorms.filter((s) => s.type === 'hail').length;
  const storms = filter === 'all' ? allStorms : allStorms.filter((s) => s.type === filter);

  return (
    <div className="card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
        <h2 className="card-title" style={{ margin: 0 }}>{t('wx.title')}</h2>
        {tab === 'noaa' && (
          <button className="btn ghost sm" disabled={busy} onClick={fetchNow}>
            {busy ? t('wx.loading') : w?.cached ? t('wx.refresh') : t('wx.load')}
          </button>
        )}
      </div>

      {/* Pestañas NOAA (búsqueda automática) | Others (reportes de clima subidos). */}
      <div style={{ display: 'inline-flex', border: '1px solid var(--line)', borderRadius: 8, overflow: 'hidden', margin: '10px 0' }}>
        <button className={`btn ghost sm${tab === 'noaa' ? ' active' : ''}`} style={{ borderRadius: 0 }} onClick={() => setTab('noaa')}><Satellite size={14} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> NOAA</button>
        <button className={`btn ghost sm${tab === 'others' ? ' active' : ''}`} style={{ borderRadius: 0 }} onClick={() => setTab('others')}><FileText size={14} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('wx.others')}{reports.length ? ` (${reports.length})` : ''}</button>
      </div>

      {/* ── Pestaña Others: reportes de clima subidos (PDF embebido). ── */}
      {tab === 'others' && (
        <div style={{ display: 'grid', gap: 10 }}>
          <div className="muted" style={{ fontSize: 'var(--fs-xs)' }}>{t('wx.othersHint')}</div>
          <FileDrop onFile={uploadReport} accept="application/pdf,image/*" label={t('wx.uploadReport')} busy={busyUp} busyLabel={t('cd.reading')} compact />
          {reports.length === 0 ? <div className="muted" style={{ fontSize: 'var(--fs-xs)' }}>{t('wx.noOthers')}</div> : reports.map((r) => (
            <details key={r.id} style={{ border: '1px solid var(--line)', borderRadius: 10 }}>
              <summary style={{ cursor: 'pointer', fontSize: 'var(--fs-xs)', fontWeight: 'var(--fw-semibold)', padding: '8px 10px', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <FileText size={14} strokeWidth={1.75} style={{ flexShrink: 0 }} /> {r.file_name || r.doc_label || `report-${r.id}`}
                <span style={{ flex: 1 }} />
                <a href={`/api/files/${r.id}/url`} target="_blank" rel="noreferrer" className="btn ghost sm" style={{ fontSize: 'var(--fs-2xs)' }} onClick={(e) => e.stopPropagation()}>↗ {t('common.open')}</a>
                <span className="muted" style={{ fontSize: 'var(--fs-2xs)', fontWeight: 'var(--fw-body)' }}>{r.uploaded_by ? `${r.uploaded_by} · ` : ''}{new Date(r.created_at).toLocaleDateString(localeUI())}</span>
              </summary>
              {r.ai_summary && <div className="muted" style={{ fontSize: 'var(--fs-2xs)', padding: '0 10px 6px' }}>{r.ai_summary}</div>}
              <iframe title={`wr-${r.id}`} src={`/api/files/${r.id}/url`} style={{ width: '100%', height: 480, border: 'none', borderTop: '1px solid var(--line)', background: 'var(--card)' }} />
            </details>
          ))}
        </div>
      )}

      {tab === 'noaa' && (<>

      {w?.no_geo && <div className="muted" style={{ marginTop: 8 }}>{t('wx.noGeo')}</div>}
      {!w?.cached && !busy && !w?.no_geo && <div className="muted" style={{ marginTop: 8, fontSize: 'var(--fs-sm)' }}>{t('wx.hint')}</div>}
      {busy && <div className="muted" style={{ marginTop: 8, fontSize: 'var(--fs-sm)' }}>{t('wx.slow')}</div>}

      {/* Pestañas de filtro: All / Wind / Hail. */}
      {allStorms.length > 0 && (
        <div style={{ display: 'inline-flex', border: '1px solid var(--line)', borderRadius: 8, overflow: 'hidden', marginTop: 12 }}>
          <button className={`btn ghost sm${filter === 'all' ? ' active' : ''}`} style={{ borderRadius: 0 }} onClick={() => setFilter('all')}>{t('wx.all')} {allStorms.length}</button>
          <button className={`btn ghost sm${filter === 'wind' ? ' active' : ''}`} style={{ borderRadius: 0 }} onClick={() => setFilter('wind')}><Wind size={14} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('wx.wind')} {windCount}</button>
          <button className={`btn ghost sm${filter === 'hail' ? ' active' : ''}`} style={{ borderRadius: 0 }} onClick={() => setFilter('hail')}><CloudHail size={14} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('wx.hail')} {hailCount}</button>
        </div>
      )}

      {w?.cached && allStorms.length === 0 && <div className="muted" style={{ marginTop: 8 }}>{t('wx.none')}</div>}
      {w?.cached && allStorms.length > 0 && storms.length === 0 && <div className="muted" style={{ marginTop: 8 }}>{t('wx.noneFilter')}</div>}

      {storms.length > 0 && (
        <div style={{ display: 'grid', gap: 6, marginTop: 12, maxHeight: 340, overflowY: 'auto', paddingRight: 4 }}>
          {storms.map((s, i) => {
            const sev = severity(s);
            const isMain = w?.main_storm_date === s.date;
            const potential = i === 0 && !w?.main_storm_date;
            return (
              <div key={`${s.date}-${i}`} style={{
                display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderRadius: 10,
                border: `1px solid ${isMain ? 'var(--accent)' : 'var(--line)'}`,
                background: isMain ? 'var(--accent-soft)' : undefined,
              }}>
                {s.type === 'hail' ? <CloudHail size={18} style={{ color: sev.color, flexShrink: 0 }} /> : <Wind size={18} style={{ color: sev.color, flexShrink: 0 }} />}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600 }}>
                    {new Date(`${s.date}T12:00:00`).toLocaleDateString(localeUI())}
                    {potential && <span className="badge" style={{ marginLeft: 8, background: '#fdecea', color: '#c0392b' }}>{t('wx.potential')}</span>}
                    {isMain && <span className="badge teal" style={{ marginLeft: 8 }}>{t('wx.main')}</span>}
                  </div>
                  <div className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>
                    {s.type === 'hail' ? `${t('wx.hail')} ${s.hailSizeInches ? `${s.hailSizeInches}"` : ''}` : `${t('wx.wind')} ${s.windMph ? `${s.windMph} mph` : ''}`}
                    {' · '}{s.distanceMiles} mi · <span style={{ color: sev.color }}>{sev.label}</span>
                  </div>
                </div>
                {!isMain && <button className="btn ghost sm" onClick={() => setMain(s.date, s.type)}>{t('wx.setMain')}</button>}
              </div>
            );
          })}
        </div>
      )}

      </>)}
    </div>
  );
}
